import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { sortRegions } from '../utils/sort';
import PageLayout from '../components/PageLayout';
import { VenueBrandingThumb, VenueNameWithBranding } from '../components/VenueBrandingPreview';
import './VenueAllowlistPage.css';

interface VenueAllowlist {
  region_name: string;
  allowlist_entry_id: string; // UUID for allowlist entry
  name: string;
  normalized_name: string;
  ticketmaster_id?: string; // Comma-separated for multiple IDs
  etix_id?: string; // Comma-separated for multiple IDs
  eventim_id?: string; // Comma-separated for multiple IDs
  scraper_id?: string; // Scraper config id (e.g. lh-st.com)
  ticketmaster_id_count?: number; // Count of Ticketmaster IDs from mapping table
  etix_id_count?: number; // Count of Etix IDs from mapping table
  eventim_id_count?: number; // Count of EventIM IDs from mapping table
  scraper_id_count?: number; // Count of Scraper IDs from mapping table (0 or 1)
  map_marker?: string; // For initialization when venue is created
  enabled: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

const VenueAllowlistPage: React.FC = () => {
  const { token } = useAuth();
  const [regions, setRegions] = useState<Array<{ region_name: string; metro_name: string }>>([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [venues, setVenues] = useState<VenueAllowlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState<VenueAllowlist | null>(null);
  const [formData, setFormData] = useState<Partial<VenueAllowlist>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEnabled, setFilterEnabled] = useState<boolean | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!token) {
      setRegionsLoading(false);
      return;
    }
    const loadRegions = async () => {
      setRegionsLoading(true);
      try {
        const response = await apiRequest<{ regions: Array<{ region_name: string; metro_name: string }> | null; count: number }>(
          '/admin/regions',
          token
        );
        const list = sortRegions(response.regions || []);
        setRegions(list);
        setSelectedRegion((prev) => {
          if (list.length === 0) return '';
          if (prev && list.some((r) => r.region_name === prev)) return prev;
          return list[0].region_name;
        });
      } catch (err) {
        console.error('Failed to load regions:', err);
        setRegions([]);
      } finally {
        setRegionsLoading(false);
      }
    };
    loadRegions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && selectedRegion) {
      loadVenues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedRegion]);

  const loadVenues = async () => {
    if (!token || !selectedRegion) return;
    try {
      setLoading(true);
      const response = await apiRequest<{ venues: VenueAllowlist[] | null; count: number }>(
        `/admin/venues/allowlist?region=${selectedRegion}`,
        token
      );
      setVenues(response.venues || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load allowlisted venues');
      setVenues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingVenue(null);
    setFormData({
      region_name: selectedRegion,
      enabled: true,
    });
    setShowForm(true);
  };

  const handleEdit = (venue: VenueAllowlist) => {
    setEditingVenue(venue);
    setFormData(venue);
    setShowForm(true);
  };

  const handleDelete = async (venue: VenueAllowlist) => {
    if (!token || !window.confirm(`Are you sure you want to delete "${venue.name}" from the allowlist?`)) {
      return;
    }
    try {
      await apiRequest(`/admin/venues/allowlist/${venue.region_name}/${venue.allowlist_entry_id}`, token, {
        method: 'DELETE',
      });
      await loadVenues();
    } catch (err: any) {
      setError(err.message || 'Failed to delete venue');
    }
  };

  const handleToggleEnabled = async (venue: VenueAllowlist) => {
    if (!token) return;
    try {
      await apiRequest(`/admin/venues/allowlist/${venue.region_name}/${venue.allowlist_entry_id}`, token, {
        method: 'PUT',
        body: JSON.stringify({
          ...venue,
          enabled: !venue.enabled,
        }),
      });
      await loadVenues();
    } catch (err: any) {
      setError(err.message || 'Failed to update venue');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      const payload = {
        ...formData,
        region_name: selectedRegion,
      };

      if (editingVenue) {
        await apiRequest(
          `/admin/venues/allowlist/${editingVenue.region_name}/${editingVenue.allowlist_entry_id}`,
          token,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        );
      } else {
        await apiRequest('/admin/venues/allowlist', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setShowForm(false);
      await loadVenues();
    } catch (err: any) {
      setError(err.message || 'Failed to save venue');
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  // Filter venues based on search term and enabled status, then sort
  const filteredVenues = venues.filter((venue) => {
    const matchesSearch = searchTerm === '' || 
      venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (venue.ticketmaster_id && venue.ticketmaster_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (venue.scraper_id && venue.scraper_id.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesEnabled = filterEnabled === null || venue.enabled === filterEnabled;
    return matchesSearch && matchesEnabled;
  });

  const displayedVenues =
    filteredVenues.length === 0
      ? filteredVenues
      : [...filteredVenues].sort((a, b) => {
          let aValue: string;
          let bValue: string;
          switch (sortColumn) {
            case 'name':
              aValue = (a.name || '').toLowerCase();
              bValue = (b.name || '').toLowerCase();
              break;
            case 'entry_id':
              aValue = (a.allowlist_entry_id || '').toLowerCase();
              bValue = (b.allowlist_entry_id || '').toLowerCase();
              break;
            case 'ticketmaster_id':
              aValue = (a.ticketmaster_id || '').toLowerCase();
              bValue = (b.ticketmaster_id || '').toLowerCase();
              break;
            case 'etix_id':
              aValue = (a.etix_id || '').toLowerCase();
              bValue = (b.etix_id || '').toLowerCase();
              break;
            case 'eventim_id':
              aValue = (a.eventim_id || '').toLowerCase();
              bValue = (b.eventim_id || '').toLowerCase();
              break;
            case 'scraper_id':
              aValue = (a.scraper_id || '').toLowerCase();
              bValue = (b.scraper_id || '').toLowerCase();
              break;
            case 'status':
              aValue = a.enabled ? 'enabled' : 'disabled';
              bValue = b.enabled ? 'enabled' : 'disabled';
              break;
            default:
              return 0;
          }
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });

  if (regionsLoading || (loading && venues.length === 0 && selectedRegion)) {
    return (
      <PageLayout title="Venue Allowlist">
        <div>Loading...</div>
      </PageLayout>
    );
  }

  if (!regionsLoading && regions.length === 0) {
    return (
      <PageLayout title="Venue Allowlist">
        <div className="error-banner">No regions found. Add regions in the Regions admin page.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Venue Allowlist">
      {error && <div className="error-banner">{error}</div>}
      
      <div className="page-actions">
        <div className="region-selector">
          <label>Region:</label>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="region-select"
          >
            {regions.map((region) => (
              <option key={region.region_name} value={region.region_name}>
                {region.metro_name}
              </option>
            ))}
          </select>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          Add Venue
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search by name, Ticketmaster ID, or Scraper ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterEnabled === null ? 'all' : filterEnabled ? 'enabled' : 'disabled'}
          onChange={(e) => {
            if (e.target.value === 'all') setFilterEnabled(null);
            else setFilterEnabled(e.target.value === 'enabled');
          }}
          className="filter-select"
        >
          <option value="all">All</option>
          <option value="disabled">Disabled Only</option>
          <option value="enabled">Enabled Only</option>
        </select>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <VenueNameWithBranding
              name={formData.name || (editingVenue ? 'Edit Allowlisted Venue' : 'Add Venue to Allowlist')}
              mapMarker={formData.map_marker}
            />
            <form onSubmit={handleSubmit}>
              {editingVenue && (
                <div className="form-group">
                  <label>Entry ID</label>
                  <input
                    type="text"
                    value={editingVenue.allowlist_entry_id || ''}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', color: '#666', fontFamily: 'monospace', fontSize: '0.875rem' }}
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem' }}>
                    UUID for this allowlist entry (read-only)
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Ticketmaster ID(s)</label>
                  <textarea
                    value={formData.ticketmaster_id || ''}
                    onChange={(e) => setFormData({ ...formData, ticketmaster_id: e.target.value })}
                    placeholder="Single ID or comma-separated list (e.g., KovZ..., KovZ...)"
                    rows={2}
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem' }}>
                    Enter single ID or comma-separated list for multiple Ticketmaster IDs
                  </small>
                </div>
                <div className="form-group">
                  <label>Etix ID(s)</label>
                  <textarea
                    value={formData.etix_id || ''}
                    onChange={(e) => setFormData({ ...formData, etix_id: e.target.value })}
                    placeholder="Single ID or comma-separated list (e.g., 12345, 67890)"
                    rows={2}
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem' }}>
                    Enter single ID or comma-separated list for multiple Etix IDs
                  </small>
                </div>
              </div>
              <div className="form-group">
                <label>EventIM ID(s)</label>
                <textarea
                  value={formData.eventim_id || ''}
                  onChange={(e) => setFormData({ ...formData, eventim_id: e.target.value })}
                  placeholder="Single ID or comma-separated list (e.g., 12345, 67890, 11111, ...)"
                  rows={3}
                />
                <small style={{ color: '#666', fontSize: '0.875rem' }}>
                  Enter single ID or comma-separated list for multiple EventIM IDs (e.g., 12 IDs for Subterranean)
                </small>
              </div>
              <div className="form-group">
                <label>Scraper ID</label>
                <input
                  type="text"
                  placeholder="e.g. lh-st.com (scraper config id)"
                  value={formData.scraper_id || ''}
                  onChange={(e) => setFormData({ ...formData, scraper_id: e.target.value })}
                />
                <small style={{ color: '#666', fontSize: '0.875rem' }}>
                  Scraper config id for this venue (e.g. lh-st.com). Enables scraper ingestion for this venue.
                </small>
              </div>
              <div className="form-group">
                <label>Map Marker (for initialization)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="e.g., venue-map-marker.png"
                    value={formData.map_marker || ''}
                    onChange={(e) => setFormData({ ...formData, map_marker: e.target.value })}
                    style={{ flex: 1 }}
                  />
                  <VenueBrandingThumb storedValue={formData.map_marker} alt="Map marker preview" size="lg" />
                </div>
                <small>Optional: Pre-populates map_marker when venue is first created</small>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.enabled !== false}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                  Enabled
                </label>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingVenue ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
              <tr>
                <th
                  onClick={() => handleSort('name')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Name {getSortIcon('name') && <span className="sort-arrow">{getSortIcon('name')}</span>}
                </th>
                <th
                  onClick={() => handleSort('entry_id')}
                  style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                  Entry ID {getSortIcon('entry_id') && <span className="sort-arrow">{getSortIcon('entry_id')}</span>}
                </th>
                <th
                  onClick={() => handleSort('ticketmaster_id')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Ticketmaster ID(s){' '}
                  {getSortIcon('ticketmaster_id') && (
                    <span className="sort-arrow">{getSortIcon('ticketmaster_id')}</span>
                  )}
                </th>
                <th
                  onClick={() => handleSort('etix_id')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Etix ID(s) {getSortIcon('etix_id') && <span className="sort-arrow">{getSortIcon('etix_id')}</span>}
                </th>
                <th
                  onClick={() => handleSort('eventim_id')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  EventIM ID(s){' '}
                  {getSortIcon('eventim_id') && <span className="sort-arrow">{getSortIcon('eventim_id')}</span>}
                </th>
                <th
                  onClick={() => handleSort('scraper_id')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Scraper ID {getSortIcon('scraper_id') && <span className="sort-arrow">{getSortIcon('scraper_id')}</span>}
                </th>
                <th
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Status {getSortIcon('status') && <span className="sort-arrow">{getSortIcon('status')}</span>}
                </th>
                <th>Actions</th>
              </tr>
          </thead>
          <tbody>
            {displayedVenues.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>
                  {searchTerm || filterEnabled !== null
                    ? 'No venues match the current filters'
                    : 'No venues in allowlist for this region'}
                </td>
              </tr>
            ) : (
              displayedVenues.map((venue) => (
                <tr key={`${venue.region_name}-${venue.allowlist_entry_id}`} className={!venue.enabled ? 'disabled-row' : ''}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <VenueBrandingThumb storedValue={venue.map_marker} alt="" size="sm" />
                      {venue.name}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.875rem', color: '#666', fontFamily: 'monospace' }}>
                    {venue.allowlist_entry_id}
                  </td>
                  <td>
                    {venue.ticketmaster_id || '-'}
                    {venue.ticketmaster_id_count !== undefined && venue.ticketmaster_id_count > 0 && (
                      <span style={{ color: '#666', fontSize: '0.875rem', marginLeft: '8px' }}>
                        ({venue.ticketmaster_id_count})
                      </span>
                    )}
                  </td>
                  <td>
                    {venue.etix_id || '-'}
                    {venue.etix_id_count !== undefined && venue.etix_id_count > 0 && (
                      <span style={{ color: '#666', fontSize: '0.875rem', marginLeft: '8px' }}>
                        ({venue.etix_id_count})
                      </span>
                    )}
                  </td>
                  <td>
                    {venue.eventim_id || '-'}
                    {venue.eventim_id_count !== undefined && venue.eventim_id_count > 0 && (
                      <span style={{ color: '#666', fontSize: '0.875rem', marginLeft: '8px' }}>
                        ({venue.eventim_id_count})
                      </span>
                    )}
                  </td>
                  <td>
                    {venue.scraper_id || '-'}
                    {venue.scraper_id_count !== undefined && venue.scraper_id_count > 0 && (
                      <span style={{ color: '#666', fontSize: '0.875rem', marginLeft: '8px' }}>
                        ({venue.scraper_id_count})
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${venue.enabled ? 'enabled' : 'disabled'}`}>
                      {venue.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div className="actions-buttons">
                      <button type="button" onClick={() => handleEdit(venue)} className="btn-sm">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleEnabled(venue)}
                        className="btn-sm btn-warning"
                      >
                        {venue.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button type="button" onClick={() => handleDelete(venue)} className="btn-sm btn-danger">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
};

export default VenueAllowlistPage;

